import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {LoadingService} from './loading.service';
import * as Sentry from '@sentry/angular';
import {TeamService} from './team.service';
import {del, get, patch, post, put} from 'aws-amplify/api';
import {fetchAuthSession} from 'aws-amplify/auth';
type GetOperation = ReturnType<typeof get>;
type PostOperation = ReturnType<typeof post>;
type PutOperation = ReturnType<typeof put>;
type PatchOperation = ReturnType<typeof patch>;
type DeleteOperation = ReturnType<typeof del>;

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  apiName = Object.keys(environment.amplify.API.REST)[0];

  constructor(
    private loadingService: LoadingService,
    private teamService: TeamService,
  ) {
  }

  async get<T>(teamId: number, path: string, hideLoading?: boolean): Promise<T> {
    try {
      const init = await this.getInitParams();
      const operation: GetOperation = get({
          apiName: this.apiName,
          path: `team-admin/${teamId}/${path}`,
          options: init
        }
      );

      if (hideLoading) {
        return await (await operation.response).body.json() as T;
      }
      try {
        this.loadingService.show();
        return await (await operation.response).body.json() as T;
      } finally {
        this.loadingService.hide();
      }
    } catch (e: any) {
      Sentry.captureException(e.response);
      throw e;
    }
  }

  async post<T>(teamId: number, path: string, body: any, hideLoading?: boolean, forceExecution?: boolean): Promise<T> {
    if (!forceExecution && !(await this.teamService.checkActiveStatus())) {
      throw new Error('Team status is not active');
    }

    try {
      const init = await this.getInitParams(body);
      const operation: PostOperation = post({
          apiName: this.apiName,
          path: `team-admin/${teamId}/${path}`,
          options: init
        }
      );

      if (hideLoading) {
        return await (await operation.response).body.json() as T;
      }

      try {
        this.loadingService.show();
        return await (await operation.response).body.json() as T;
      } finally {
        this.loadingService.hide();
      }
    } catch (e: any) {
      Sentry.captureException(e.response);
      throw e;
    }
  }

  async put<T>(teamId: number, path: string, body: any, hideLoading?: boolean, forceExecution?: boolean): Promise<T> {
    if (!forceExecution && !(await this.teamService.checkActiveStatus())) {
      throw new Error('Team status is not active');
    }

    try {
      const init = await this.getInitParams(body);
      const operation: PutOperation = put({
          apiName: this.apiName,
          path: `team-admin/${teamId}/${path}`,
          options: init
        }
      );

      if (hideLoading) {
        return await (await operation.response).body.json() as T;
      }
      try {
        this.loadingService.show();
        return await (await operation.response).body.json() as T;
      } finally {
        this.loadingService.hide();
      }
    } catch (e: any) {
      Sentry.captureException(e.response);
      throw e;
    }
  }

  async patch<T>(teamId: number, path: string, body: any, hideLoading?: boolean, forceExecution?: boolean): Promise<T> {
    if (!forceExecution && !(await this.teamService.checkActiveStatus())) {
      throw new Error('Team status is not active');
    }

    try {
      const init = await this.getInitParams(body);
      const operation: PatchOperation = patch({
          apiName: this.apiName,
          path: `team-admin/${teamId}/${path}`,
          options: init
        }
      );

      if (hideLoading) {
        return await (await operation.response).body.json() as T;
      }
      try {
        this.loadingService.show();
        return await (await operation.response).body.json() as T;
      } finally {
        this.loadingService.hide();
      }
    } catch (e: any) {
      Sentry.captureException(e.response);
      throw e;
    }
  }

  async delete(teamId: number, path: string, hideLoading?: boolean, forceExecution?: boolean): Promise<void> {
    if (!forceExecution && !(await this.teamService.checkActiveStatus())) {
      throw new Error('Team status is not active');
    }

    try {
      const init = await this.getInitParams();
      const operation: DeleteOperation = del({
          apiName: this.apiName,
          path: `team-admin/${teamId}/${path}`,
          options: init
        }
      );

      if (hideLoading) {
        await operation.response;
        return;
      }
      try {
        this.loadingService.show();
        await operation.response
      } finally {
        this.loadingService.hide();
      }
    } catch (e: any) {
      Sentry.captureException(e.response);
      throw e;
    }
  }

  private async getInitParams(body?: any) {
    const init: any = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${(await fetchAuthSession()).tokens?.idToken?.toString()}`,
      }
    };
    if (!!body) {
      init.body = body;
    }
    return init;
  }
}

